<template>
  <div>
    <v-badge
      :title="i18n.global.t('app.pdfHistory.tooltip.newPdf')"
      :offset-y="6"
      :offset-x="6"
      color="primary"
      dot
      :model-value="labelData.isNew"
    >
      <v-btn
        @click="download()"
        variant="text"
        color="primary"
        :icon="loadingState === 'success' ? 'mdi-check' : 'mdi-download'"
        :loading="loadingState === 'loading'"
      ></v-btn>
    </v-badge>
    <v-snackbar v-model="showDownloadInfo" :timeout="5000">
      {{
        i18n.global.t("app.pdfHistory.loadingDownload.success", {
          pdf: labelData.Layout,
        })
      }}
      <template v-slot:actions>
        <v-btn color="primary" variant="text" @click="showDownloadInfo = false">
          {{ i18n.global.t('global.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="showDownloadError" :timeout="5000">
      {{
        i18n.global.t("app.pdfHistory.loadingDownload.error", {
          pdf: labelData.Layout,
        })
      }}
      <template v-slot:actions>
        <v-btn color="primary" variant="text" @click="showDownloadError = false">
          {{ i18n.global.t('global.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script setup lang="ts">
import { PropType, ref } from "vue";
import { PrintItems } from "@/api-client/responses";
import { ApiClient } from "@/api-client";
import {
  useSystemStore,
  useSupplierStore,
  useMaterialStore,
  useApplicationStore,
} from "@/store";
import { i18n } from "@/locales";

interface NewAndOldPdfIndicator extends PrintItems.PrintItemPdf {
  isNew: boolean;
}

const props = defineProps({
  labelData: {
    required: true,
    type: Object as PropType<NewAndOldPdfIndicator>,
  },
});

const emits = defineEmits(["download"]);

const systemStore = useSystemStore();
const supplierStore = useSupplierStore();
const materialStore = useMaterialStore();
const apiClient = ApiClient.getInstance();
const applicationStore = useApplicationStore();

const loadingState = ref<loadingStateType>("unset");
const showDownloadInfo = ref(false);
const showDownloadError = ref(false);

async function download() {
  if (
    !supplierStore.selectedSupplier ||
    !systemStore.selectedSystem ||
    !materialStore.selectedMatNr
  ) {
    console.warn(`No Supplier selected!`);
    return;
  }

  loadingState.value = "loading";
  emits("download");

  const material = materialStore.selectedMatNr;
  const supplier = supplierStore.selectedSupplier.SupplierId;
  const originalSupplier = systemStore.selectedSystem.supplierId;
  const system = systemStore.selectedSystem.system;
  const labelDate = new Date(Number(props.labelData.Date));

  try {
    await apiClient.downloadPdfHistoryFile(
      material,
      supplier,
      originalSupplier,
      props.labelData.Layout,
      system,
      props.labelData.PrintItemId,
      `${labelDate.getFullYear()}.${(labelDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}.${labelDate.getDate().toString().padStart(2, "0")}`,
      Number(props.labelData.Quantity)
    );
  } catch (error: unknown) {
    applicationStore.addAlert({
      key: `app.pdfHistory.loadingDownload.error`,
      keyData: {
        message: error instanceof Error ? error.message : String(error),
      },
      type: "Alert",
    });
    loadingState.value = "failed";
    showDownloadError.value = true;
    return;
  }
  loadingState.value = "success";
  showDownloadInfo.value = true;
}
</script>
<style lang="scss" scoped></style>
